import type { QueryClient } from '@tanstack/react-query'
import type { Board, Comment, Subtask, Task, TaskDetail } from './types'

/**
 * Making the interface answer immediately.
 *
 * Every edit used to cost two round trips: the mutation, then an invalidation
 * that fetched back what the server had just told us. On a laptop next to the
 * server that reads as a stutter; over a real connection it is half a second
 * of a board that has not noticed you did anything.
 *
 * So changes are applied to the cache first and sent afterwards. Three rules
 * keep that honest:
 *
 *   1. Cancel in-flight reads before patching. A GET that was already on its
 *      way will happily land on top of the optimistic state and undo it.
 *   2. On success, splice in what the server returned rather than invalidating.
 *      It is the authoritative version and we are already holding it; fetching
 *      it again would only produce the same bytes and a flash.
 *   3. On failure, put back the exact snapshot taken in step 1, and say so.
 *      Silently reverting is worse than not being optimistic at all.
 *
 * One task appears in several caches at once — every filtered variant of a
 * board, the flat lists, quick search, and the drawer's detail — so patches go
 * through here rather than each mutation remembering the full set.
 */

/** The cache keys a task can appear under. */
const TASK_LIST_KEYS = [['board'], ['tasks'], ['search']] as const

export type Snapshot = Array<[readonly unknown[], unknown]>

/**
 * Records every cache a task change could touch, so a failure can put all of
 * them back exactly as they were.
 */
export function snapshotTasks(qc: QueryClient, taskId?: string): Snapshot {
  const entries: Snapshot = []
  for (const key of TASK_LIST_KEYS) {
    for (const [k, v] of qc.getQueriesData({ queryKey: key })) entries.push([k, v])
  }
  if (taskId) {
    const key = ['task', taskId]
    entries.push([key, qc.getQueryData(key)])
  }
  return entries
}

export function restore(qc: QueryClient, snapshot: Snapshot) {
  for (const [key, value] of snapshot) qc.setQueryData(key, value)
}

/**
 * Stops any read that is already in flight, so it cannot overwrite what we are
 * about to write. Callers await this before patching.
 */
export function haltReads(qc: QueryClient, taskId?: string) {
  const waits = TASK_LIST_KEYS.map((key) => qc.cancelQueries({ queryKey: key }))
  if (taskId) waits.push(qc.cancelQueries({ queryKey: ['task', taskId] }))
  return Promise.all(waits)
}

// ------------------------------------------------------------------- tasks

/** Applies a change to one task wherever it is cached, detail included. */
export function patchTask(qc: QueryClient, taskId: string, change: (task: Task) => Task) {
  for (const key of TASK_LIST_KEYS) {
    qc.setQueriesData({ queryKey: key }, (old: unknown) => mapTasks(old, taskId, change))
  }
  qc.setQueryData(['task', taskId], (old: TaskDetail | undefined) =>
    old ? ({ ...change(old), ...detailOnly(old) } as TaskDetail) : old,
  )
}

/** Drops a task from every list. The detail cache is left for the drawer. */
export function removeTask(qc: QueryClient, taskId: string) {
  for (const key of TASK_LIST_KEYS) {
    qc.setQueriesData({ queryKey: key }, (old: unknown) => filterTasks(old, taskId))
  }
}

/**
 * Puts a task into the boards of the project it belongs to.
 *
 * Only boards, and only unfiltered ones: a card that does not match the filter
 * you are looking at should not appear just because you made it, and working
 * out whether it matches would mean reimplementing the server's filter in the
 * browser. Filtered views correct themselves on the next background read.
 */
export function insertTask(qc: QueryClient, task: Task) {
  qc.setQueriesData({ queryKey: ['board'] }, (old: unknown) => {
    const board = old as Board | undefined
    if (!board || !Array.isArray(board.tasks)) return old
    if (board.tasks.some((t) => t.id === task.id)) return old

    const belongs =
      board.project?.id === task.projectId ||
      board.children?.some((c) => c.id === task.projectId)
    if (!belongs) return old

    return { ...board, tasks: [...board.tasks, task], counts: bumpCount(board, task, 1) }
  })
}

/** Keeps a column header agreeing with the cards under it. */
function bumpCount(board: Board, task: Task, by: number): Record<string, number> {
  const key = board.aggregated ? task.statusName.toLowerCase() : task.statusId
  const counts = { ...(board.counts ?? {}) }
  counts[key] = Math.max(0, (counts[key] ?? 0) + by)
  return counts
}

/**
 * Moves a task between columns in the cached boards, fixing both headers.
 *
 * The board component drags its own local copy, so this exists for the counts
 * and for every *other* cached board that shows the same card.
 */
export function moveTaskInBoards(
  qc: QueryClient,
  taskId: string,
  to: { statusId: string; statusName: string; statusDone: boolean },
) {
  qc.setQueriesData({ queryKey: ['board'] }, (old: unknown) => {
    const board = old as Board | undefined
    if (!board || !Array.isArray(board.tasks)) return old
    const current = board.tasks.find((t) => t.id === taskId)
    if (!current) return old

    let counts = bumpCount(board, current, -1)
    const moved = { ...current, ...to }
    const key = board.aggregated ? moved.statusName.toLowerCase() : moved.statusId
    counts = { ...counts, [key]: (counts[key] ?? 0) + 1 }

    return {
      ...board,
      tasks: board.tasks.map((t) => (t.id === taskId ? moved : t)),
      counts,
    }
  })
}

/**
 * Finds a column by id among the boards already on screen.
 *
 * Not from the statuses query: only the settings page fills that one in, and
 * the board carries its own columns. Reading the wrong cache is why changing
 * status from the drawer used to sit and wait for the server while every other
 * field answered at once.
 */
export function findStatus(
  qc: QueryClient,
  statusId: string,
): { statusId: string; statusName: string; statusDone: boolean } | null {
  for (const [, value] of qc.getQueriesData({ queryKey: ['board'] })) {
    const board = value as Board | undefined
    const found = board?.statuses?.find((s) => s.id === statusId)
    if (found) {
      return { statusId: found.id, statusName: found.name, statusDone: found.isDone }
    }
  }
  return null
}

// ---------------------------------------------------------------- subtasks

/**
 * Rewrites a task's checklist and the two counters the card shows, so a tick
 * updates the badge on the board at the same moment as the row in the drawer.
 */
export function patchSubtasks(
  qc: QueryClient,
  taskId: string,
  change: (list: Subtask[]) => Subtask[],
) {
  qc.setQueryData(['task', taskId], (old: TaskDetail | undefined) => {
    if (!old) return old
    const subtasks = change(old.subtasks ?? [])
    return {
      ...old,
      subtasks,
      subtaskCount: subtasks.length,
      subtaskDone: subtasks.filter((s) => s.done).length,
    }
  })

  const detail = qc.getQueryData<TaskDetail>(['task', taskId])
  if (!detail) return
  patchTask(qc, taskId, (t) => ({
    ...t,
    subtaskCount: detail.subtaskCount,
    subtaskDone: detail.subtaskDone,
  }))
}

// ---------------------------------------------------------------- comments

export function patchComments(
  qc: QueryClient,
  taskId: string,
  change: (list: Comment[]) => Comment[],
) {
  qc.setQueryData(['task', taskId], (old: TaskDetail | undefined) => {
    if (!old) return old
    const comments = change(old.comments ?? [])
    return { ...old, comments, commentCount: comments.length }
  })
  const detail = qc.getQueryData<TaskDetail>(['task', taskId])
  if (detail) patchTask(qc, taskId, (t) => ({ ...t, commentCount: detail.commentCount }))
}

// ----------------------------------------------------------------- helpers

/**
 * A placeholder id for something the server has not seen yet.
 *
 * Prefixed rather than a bare uuid so anything holding one can tell: an
 * optimistic card must not open a drawer for an id that does not exist.
 */
export function tempId(): string {
  return `pending-${crypto.randomUUID()}`
}

export function isPending(id: string): boolean {
  return id.startsWith('pending-')
}

/** Applies the same field changes the server will, so the two agree. */
export function applyPatch(task: Task, body: Record<string, unknown>): Task {
  const next: Task = { ...task }
  if (typeof body.title === 'string') next.title = body.title
  if (typeof body.description === 'string') next.description = body.description
  if (typeof body.priority === 'string') next.priority = body.priority as Task['priority']
  if ('dueDate' in body) next.dueDate = (body.dueDate as string | null) ?? null
  return next
}

function detailOnly(detail: TaskDetail) {
  return {
    subtasks: detail.subtasks,
    comments: detail.comments,
    attachments: detail.attachments,
    activities: detail.activities,
    watchers: detail.watchers,
    isWatching: detail.isWatching,
  }
}

/** Boards carry tasks under `.tasks`; the flat lists are arrays themselves. */
function mapTasks(old: unknown, taskId: string, change: (t: Task) => Task): unknown {
  if (Array.isArray(old)) {
    return old.map((t: Task) => (t?.id === taskId ? change(t) : t))
  }
  const board = old as Board | undefined
  if (board && Array.isArray(board.tasks)) {
    return { ...board, tasks: board.tasks.map((t) => (t.id === taskId ? change(t) : t)) }
  }
  return old
}

function filterTasks(old: unknown, taskId: string): unknown {
  if (Array.isArray(old)) return old.filter((t: Task) => t?.id !== taskId)
  const board = old as Board | undefined
  if (board && Array.isArray(board.tasks)) {
    const going = board.tasks.find((t) => t.id === taskId)
    return {
      ...board,
      tasks: board.tasks.filter((t) => t.id !== taskId),
      counts: going ? bumpCount(board, going, -1) : board.counts,
    }
  }
  return old
}
